<template>
  <div class="overflow-y-hidden w-full h-[100lvh] bg-cw-green-dark flex flex-col fixed left-0 top-0 z-50">
    <div class="px-40 py-20 heightMd:pt-8 heightSm:pt-8 grid grid-cols-[2fr_4fr_2fr] gap-3">
      <aside class="flex flex-col items-center justify-center h-full">
        <TradeSwapCards :cardToTrade :letter="tradeLetter" :currentTypeCard />
      </aside>
      <main class="flex flex-col items-center justify-center h-full">
        <h1 class="text-3xl font-bold text-white mb-5">Choose a letter you want</h1>
        <div class="flex gap-3 mt-4 font-bold">
          <button
            class="px-12 py-3.5 heightMd:px-7 heightMd:py-2 bg-cw-green-alt tracking-normal rounded-full heightMd:text-sm transform transition-all duration-200 border-3 border-cw-green-alt"
            :class="tradeCardType === 'plan' ? 'bg-white text-cw-green-dark !border-cw-green ' : 'text-white'"
            @click="selectOption('plan')"
          >
            Plan
          </button>
          <button
            class="px-12 py-3.5 heightMd:px-7 heightMd:py-2 bg-cw-green-alt tracking-normal rounded-full heightMd:text-sm transform transition-all duration-200 border-3 border-cw-green-alt"
            :class="tradeCardType === 'save' ? 'bg-white text-cw-green-dark !border-cw-green' : 'text-white'"
            @click="selectOption('save')"
          >
            Save
          </button>
          <button
            class="px-12 py-3.5 heightMd:px-7 heightMd:py-2 bg-cw-green-alt tracking-normal rounded-full heightMd:text-sm transform transition-all duration-200 border-3 border-cw-green-alt"
            :class="tradeCardType === 'invest' ? 'bg-white text-cw-green-dark !border-cw-green' : 'text-white'"
            @click="selectOption('invest')"
          >
            Invest
          </button>
        </div>
        <div class="heightMd:mt-10 mt-16">
          <LetterInput :defaultLetter="tradeLetter" :isDisabled="isWaitingForTradeResponse" :letters="tradeCardType" mode="selection" @letterSelected="handleLetterSelected" :key="tradeCardType" />
        </div>
        <div class="mt-[120px] heightMd:mt-14 *:!w-full">
          <BaseButton
            class="heightMd:px-9 px-16 flex items-center justify-center !rounded-xl font-bold heightMd:text-sm"
            :backgroundColor="'var(--color-cw-green)'"
            borderColor="var(--color-cw-green-border-shadow)"
            @click="makeWord"
            :disabled="!tradeLetter || isWaitingForTradeResponse"
          >
            {{ isWaitingForTradeResponse ? 'Checking....' : 'Submit' }}
          </BaseButton>
        </div>
      </main>
    </div>
    <GameTimer
      :key="currentTurnPlayerProfile.actionTimeout"
      @timeUp="handleTimeout"
      :timeRemaining="getActionTimeout()"
      v-if="isTurn && currentTurnPlayerProfile && currentTurnPlayerProfile.actionTimeout > 0"
      class="absolute bottom-10 left-10"
    />

    <a href="" v-if="!isWaitingForTradeResponse" @click.prevent="goToGameAreaScreen" class="absolute heightMd:top-6 heightMd:left-14 top-12 left-20">
      <CloseIcon class="heightMd:w-6 heightMd:h-6 w-8 h-8 text-white" />
    </a>
  </div>
</template>

<script setup lang="ts">
import { ref, computed, inject } from 'vue'
import LetterInput from '@/components/game-play/LetterInput.vue'
import BaseButton from '@/components/base/BaseButton.vue'
import CloseIcon from '@/assets/svgs/icons/close.svg'
import { dummyCards } from '@/data/onboarding'
import TradeSwapCards from '@/components/game-play/TradeSwapCards.vue'
import { useGameArea } from '@/composables/useGameArea'
import { useHelpers } from '@/composables/useHelpers'
import useSocket from '@/composables/useSocket'
import { GameEvent } from '@/types/Events'
import type { CardCategoryT, CardT } from '@/types/Game'
import { useGameStore } from '@/stores/game'
import { storeToRefs } from 'pinia'
import GameTimer from '@/components/interface/GameTimer.vue'

const showLoader = inject('showLoader') as InjectedFunctionT
const hideLoader = inject('hideLoader') as InjectedFunctionT

const { gameState, currentTurnPlayerProfile, isTurn } = storeToRefs(useGameStore())
const { goToGameAreaScreen, cardToTrade, tradeLetter, tradeCardType } = useGameArea()
const { getActionTimeout } = useGameStore()
const { handleCustomError } = useHelpers()
const socket = useSocket()

const errorMessage = ref<string | null>(null)

const currentTypeCard = computed(() => {
  switch (tradeCardType.value) {
    case 'plan':
      return dummyCards.find(card => card.category === 'plan') as CardT
    case 'save':
      return dummyCards.find(card => card.category === 'save') as CardT
    case 'invest':
      return dummyCards.find(card => card.category === 'invest') as CardT
    default:
      return dummyCards.find(card => card.category === 'plan') as CardT
  }
})

const isWaitingForTradeResponse = computed(() => {
  return gameState.value?.playStatus === 'offering_trade'
})

const selectOption = (option: CardCategoryT) => {
  if (isWaitingForTradeResponse.value) return
  tradeCardType.value = option
  tradeLetter.value = ''
}

const handleLetterSelected = (letter: string) => {
  tradeLetter.value = letter
}

const makeWord = async () => {
  try {
    errorMessage.value = ''
    showLoader()
    await socket.sendEventAsync<CardT[]>(GameEvent.InitiateTrade, {
      cardId: cardToTrade.value?.id || '',
      letter: tradeLetter.value,
      category: tradeCardType.value
    })
  } catch (error: any) {
    errorMessage.value = error
    handleCustomError(error)
  } finally {
    hideLoader()
  }
}

const handleTimeout = () => {
  // Clear any error messages and return to game screen gracefully
  errorMessage.value = ''
  hideLoader() // Ensure any loading states are cleared
  goToGameAreaScreen()
}
</script>
